import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import PlatformMessagesBanner from './PlatformMessagesBanner';
import '../styles/InviteResponse.css';
import finbaseLogo from '../assets/logos/finbase-logo.svg';

const InviteResponse = () => {
  const [inviteCode, setInviteCode] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [inviteDetails, setInviteDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmailLocked, setInviteEmailLocked] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('invite_code');
    const email = params.get('email');
    if (code) setInviteCode(code);
    if (email) {
      setInviteEmail(email);
      setInviteEmailLocked(true);
    }
  }, []);

  useEffect(() => {
    const loadInviteDetails = async () => {
      if (!inviteCode || !inviteEmail) return;
      setLoading(true);
      try {
        const response = await api.get(`/acct-mgmt/v1/signup/invite?invite_code=${inviteCode}&email=${encodeURIComponent(inviteEmail)}`);
        setInviteDetails(response.data);
      } catch (err) {
        setStatus({ type: 'danger', message: err.response?.data?.error || 'Invite code is invalid or expired.' });
      } finally {
        setLoading(false);
      }
    };
    loadInviteDetails();
  }, [inviteCode, inviteEmail]);

  const declineInvite = async () => {
    setStatus({ type: '', message: '' });
    try {
      await api.post('/acct-mgmt/v1/account/invite/respond', { invite_code: inviteCode, accept: false });
      setStatus({
        type: 'success',
        message: 'Your invitation has been declined. You may proceed with creating your own account or close this window.',
      });
      setInviteCode('');
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to decline invite.' });
    }
  };

  return (
    <div className="invite-response-page d-flex flex-column justify-content-center align-items-center min-vh-100">
      <PlatformMessagesBanner surface="id_portal" />
      <div className="card invite-response-card p-4 shadow-sm">
        <div className="auth-logo">
          <img src={finbaseLogo} alt="Finbase" />
        </div>
        <h1 className="h5 fw-bold mb-2 text-center">You're Invited</h1>
        <p className="text-muted text-center">
          This invite is for {inviteDetails?.email || inviteEmail || 'your email'}. Accept to complete registration, or decline if you weren’t expecting this invite.
        </p>
        {status.message && (
          <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
        )}
        {loading && <div className="text-muted mb-3">Loading invite details...</div>}
        {inviteDetails && (
          <div className="invite-summary text-center" role="status" aria-live="polite">
            <div className="fw-semibold text-center">
              {inviteDetails.org_name || 'An organization'} has invited you to join Finbase under their Organization's account.
            </div>
          </div>
        )}
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-3">
            <label className="form-label">Invite Code</label>
            <input
              className="form-control"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Invite Email</label>
            <input
              className="form-control"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              disabled={inviteEmailLocked}
            />
          </div>
          <div className="d-grid gap-2">
            <a
              className="btn btn-primary"
              href={inviteCode && inviteEmail ? `/signup?invite_code=${inviteCode}&email=${encodeURIComponent(inviteEmail)}` : '/signup'}
            >
              Accept Invite
            </a>
            <button type="button" className="btn btn-outline-danger" onClick={() => setShowDeclineConfirm(true)}>
              Decline Invite
            </button>
          </div>
        </form>
        <div className="auth-footer">Finbase Single Sign-On: one account for all Finbase apps.</div>
      </div>
      {showDeclineConfirm && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Decline invite?</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowDeclineConfirm(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Declining is final. You can still create your own account if you choose.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeclineConfirm(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      setShowDeclineConfirm(false);
                      await declineInvite();
                    }}
                  >
                    Yes, Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InviteResponse;
